import { VARIABLES } from '@environments/environment.variables';

/**
 * Cache entry storing data with expiration metadata.
 *
 * @template T - The cached data type
 */
export interface DataCache<T> {
  /** Cache entry identifier. */
  key: string;

  /** Cached data payload. */
  data: T;

  /** Expiration timestamp in ms (0 = no expiration). */
  expiredAt: number;

  /** Cache duration in seconds. */
  expirationTime: number;

  /** Application build timestamp for cache invalidation. */
  buildTimestamp: number;
}

/**
 * Creates a cache entry with expiration.
 *
 * @param key - Cache entry identifier
 * @param data - Data to cache
 * @param expirationTime - Cache duration in seconds (0 for no expiration)
 */
export function createDataCache<T>(key: string, data: T, expirationTime: number): DataCache<T> {
  return {
    key,
    data,
    expirationTime,
    expiredAt: expirationTime === 0 ? 0 : Date.now() + expirationTime * 1000,
    buildTimestamp: VARIABLES.buildTimestamp,
  };
}

/**
 * Type guard validating that a value is a valid DataCache structure.
 */
export function isDataCache<T>(value: unknown): value is DataCache<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'key' in value &&
    'data' in value &&
    'expiredAt' in value &&
    'buildTimestamp' in value
  );
}
